import type { DraggableAttributes, DraggableSyntheticListeners } from '@dnd-kit/core';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Box, Chip, IconButton, Stack, SxProps, Typography } from '@mui/material';
import type { MouseEvent, ReactNode } from 'react';
import { useIntl } from 'react-intl';

import { Icon, Tooltip, TruncatedTooltip } from '@verantos-dev/shared-components';
import { fontSize, opacity } from '@verantos-dev/shared-ui-components/tokens';

import { useCriteriaViewCard } from './hooks/useCriteriaViewCard';
import { useCriteriaViewCardLocators } from './hooks/useCriteriaViewCardLocators';
import {
  StyledCardContainer,
  StyledDetailRow,
  StyledDetailsSection,
  StyledDragHandle,
  StyledFooterActions,
  StyledInfoSection,
  StyledReorderButton,
  StyledReorderButtons,
  StyledReorderSection,
  StyledSerialNumberText,
  StyledStatusChipsContainer,
  StyledTitleSection,
} from './styled';
import { CriteriaCardDetailsItem, CriteriaCardType, CriteriaStatusType } from './types';

export interface CriteriaViewCardProps {
  /**
   * Unique identifier for the criteria
   */
  id: string;

  /**
   * Serial number displayed in the top-left corner
   */
  serialNumber: string | number;

  /**
   * Type of the card. Cohort criteria pass a {@link CriteriaCardType}
   * (INDEX DATE, CLINICAL, DEMOGRAPHIC, …); other consumers (e.g. the
   * analytical-dataset variable card) pass a free-form label such as a data
   * format (`STRING`, `DATE`). Rendered verbatim as the grey type caption —
   * at the top row's right edge when there are no `statusChips`, or in its
   * own caption row below when `statusChips` claims that slot instead.
   */
  cardType: CriteriaCardType | string | null;

  /**
   * Title displayed prominently on the card
   */
  title: string;

  /**
   * Status chips to display alongside the serial number, at the right
   * edge of the top row
   */
  statusChips?: CriteriaStatusType[];

  /**
   * Sentence describing how an index date is picked (e.g. "Absolute first
   * occurrence"), rendered under the top row, above the title.
   */
  indexDateSelection?: string;

  /**
   * Key-value pairs for the details section
   */
  details: CriteriaCardDetailsItem[];

  /**
   * Whether to hide the reorder buttons
   */
  hideReorderButtons?: boolean;

  /**
   * Whether the card can be moved up
   */
  canMoveUp?: boolean;

  /**
   * Whether the card can be moved down
   */
  canMoveDown?: boolean;

  /**
   * Whether the card can be edited
   */
  canEdit?: boolean;

  /**
   * Whether the card can be deleted
   */
  canDelete?: boolean;

  /**
   * Callback for move up action
   */
  onMoveUp?: () => void;

  /**
   * Callback for move down action
   */
  onMoveDown?: () => void;

  /**
   * Callback for edit action
   */
  onEdit?: () => void;

  /**
   * Callback for delete action
   */
  onDelete?: () => void;

  /**
   * Callback when the card is clicked (e.g., to open preview modal)
   */
  onClick?: () => void;

  /**
   * Custom styles for the card
   */
  sx?: SxProps;
  /**
   * The change status of the cohort builder item.
   */
  changeStatus?: string;
  /**
   * Custom icon to render in place of the default edit icon.
   */
  editIcon?: ReactNode;
  /**
   * When true, both edit and delete buttons are shown but disabled (e.g. during processing).
   */
  actionsDisabled?: boolean;
  /**
   * Tooltip shown on disabled edit/delete buttons.
   */
  actionTooltip?: string;
  /**
   * When provided, makes the status chips area clickable (e.g., to change AV role).
   */
  onStatusChipClick?: (event: React.MouseEvent<HTMLElement>) => void;

  /**
   * Extra footer-action content rendered after the save-to-library
   * button. Used today by the AV-criterion container to surface the
   * project→org publish control once the criterion has a library row
   * to act on. The slot is opaque to the card — callers own the
   * action and its testid namespacing.
   */
  additionalFooterActions?: ReactNode;

  /**
   * Optional node rendered in the top row's left cluster, immediately
   * after the serial number. Used by consumers that have no status chips
   * but still want a badge next to the number (e.g. the analytical-dataset
   * variable card's `Default` pill). Ignored when `statusChips` is non-empty.
   */
  infoBadge?: ReactNode;

  /**
   * When provided, renders a grip-style drag handle next to the serial
   * number, wired to dnd-kit's pointer/keyboard sensors via the parent
   * list's `useSortable()` call (see `SortableCriteriaItem`). Omit for
   * cards that are never draggable — a pinned index-date criterion or
   * default analytical-dataset variable, or when the owning domain's
   * drag-reorder feature flag is off.
   */
  dragHandleProps?: {
    attributes: DraggableAttributes;
    listeners: DraggableSyntheticListeners;
  };
}

export const CriteriaViewCard = (props: CriteriaViewCardProps) => {
  const intl = useIntl();
  const {
    getStatusColors,
    theme,
    onMoveUpClick,
    onMoveDownClick,
    serialNumberValue,

    // props
    id,
    cardType,
    title,
    statusChips,
    details = [],
    hideReorderButtons = false,
    canMoveUp = true,
    canMoveDown = true,
    canEdit = true,
    canDelete = true,
    onEdit,
    onDelete,
    sx,
  } = useCriteriaViewCard(props);

  const locators = useCriteriaViewCardLocators(id);
  // Only surface the edit/delete affordances when a handler exists — otherwise
  // `actionsDisabled` (the read-only lock) would render a stray disabled icon
  // on cards that have no such action at all (e.g. a non-owner's cohort).
  const showEditButton = (canEdit || props.actionsDisabled) && Boolean(onEdit);
  const showDeleteButton = (canDelete || props.actionsDisabled) && Boolean(onDelete);
  const areFooterActionsVisible =
    showEditButton || showDeleteButton || Boolean(props.additionalFooterActions);

  const handleCardClick = props.onClick
    ? (e: MouseEvent) => {
        // Avoid triggering card click when interacting with buttons
        if ((e.target as HTMLElement).closest('button')) return;
        props.onClick?.();
      }
    : undefined;

  // The top row's right slot fits one badge. `statusChips` (Inclusion/
  // Exclusion, Index date, …) claims it when present; otherwise the
  // cardType caption graduates up there instead (e.g. the analytical-dataset
  // variable card's STRING/DATE/NUMERIC label, which has no status chips).
  const hasStatusChips = Boolean(statusChips?.length);
  const showCardTypeInTopRow = !hasStatusChips && Boolean(cardType);
  const showCardTypeInInfoRow = hasStatusChips && Boolean(cardType);
  const showInfoBadgeInTopRow = !hasStatusChips && Boolean(props.infoBadge);

  const hasInfoCaption = showCardTypeInInfoRow || Boolean(props.changeStatus);

  return (
    <StyledCardContainer
      data-testid={locators.containerTestId}
      sx={{
        ...sx,
        cursor: props.onClick ? 'pointer' : undefined,
        ...(props.onClick && {
          '&:hover .criteria-view-card-title': { color: theme.palette.primary.main },
        }),
      }}
      onClick={handleCardClick}
    >
      {/* Serial number, drag handle, reorder buttons, and status badge */}
      <StyledReorderSection>
        <Stack direction="row" alignItems="center" gap={2}>
          {props.dragHandleProps ? (
            <StyledDragHandle
              data-testid={locators.dragHandleTestId}
              size="small"
              disableRipple
              {...props.dragHandleProps.attributes}
              {...props.dragHandleProps.listeners}
            >
              <DragIndicatorIcon fontSize="small" />
            </StyledDragHandle>
          ) : null}
          <StyledSerialNumberText
            data-testid={locators.serialNumberTestId}
            variant="subtitle1"
            color={theme.palette.text.primary}
          >
            {serialNumberValue}
          </StyledSerialNumberText>
          {showInfoBadgeInTopRow ? props.infoBadge : null}
        </Stack>

        <Stack direction="row" alignItems="center" gap={2}>
          {!hideReorderButtons ? (
            <StyledReorderButtons>
              <StyledReorderButton
                data-testid={locators.moveUpButtonTestId}
                onClick={onMoveUpClick}
                size="small"
                disabled={!canMoveUp}
              >
                <KeyboardArrowUpIcon fontSize="small" />
              </StyledReorderButton>

              <StyledReorderButton
                data-testid={locators.moveDownButtonTestId}
                onClick={onMoveDownClick}
                size="small"
                disabled={!canMoveDown}
              >
                <KeyboardArrowDownIcon fontSize="small" />
              </StyledReorderButton>
            </StyledReorderButtons>
          ) : null}

          {hasStatusChips ? (
            <Tooltip
              id={`status-chip-tooltip-${id}`}
              title={
                props.actionsDisabled && !props.onStatusChipClick ? props.actionTooltip ?? '' : ''
              }
            >
              <StyledStatusChipsContainer
                onClick={
                  props.onStatusChipClick
                    ? (e) => {
                        e.stopPropagation();
                        props.onStatusChipClick?.(e);
                      }
                    : undefined
                }
                sx={props.onStatusChipClick ? { cursor: 'pointer' } : undefined}
              >
                {statusChips?.map((status, index) => {
                  const { color, backgroundColor } = getStatusColors(status);
                  return (
                    <Chip
                      key={`${status}-${index}`}
                      className="criteria-status-chip"
                      data-testid={locators.statusChipTestId(index)}
                      label={
                        // TODO: no VDS token for fontSize 13 — needs designer input
                        <Typography fontSize={13} fontWeight={400} color={color}>
                          {status}
                        </Typography>
                      }
                      size="small"
                      sx={{
                        backgroundColor,
                        color,
                        ...(props.onStatusChipClick && {
                          transition: 'filter 0.2s ease',
                          '&:hover': { filter: 'brightness(0.8)' },
                        }),
                      }}
                    />
                  );
                })}
              </StyledStatusChipsContainer>
            </Tooltip>
          ) : showCardTypeInTopRow ? (
            <Typography
              data-testid={locators.cardTypeTestId}
              fontSize={fontSize[12]}
              fontWeight={400}
              letterSpacing={0.4}
              color={theme.palette.text.secondary}
            >
              {cardType}
            </Typography>
          ) : null}
        </Stack>
      </StyledReorderSection>

      {/* Card type (criteria cards only) and change status */}
      {hasInfoCaption ? (
        <StyledInfoSection>
          <Stack direction="row" alignItems="center" gap={3}>
            {showCardTypeInInfoRow ? (
              <Typography
                data-testid={locators.cardTypeTestId}
                fontSize={fontSize[12]}
                fontWeight={400}
                letterSpacing={0.4}
                color={theme.palette.text.secondary}
              >
                {cardType}
              </Typography>
            ) : null}
            {props.changeStatus ? (
              <Chip
                label={props.changeStatus}
                size="small"
                sx={{
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.white.main,
                  fontSize: '13px', // TODO: no VDS token for fontSize 13 — needs designer input
                }}
              />
            ) : null}
          </Stack>
        </StyledInfoSection>
      ) : null}

      {props.indexDateSelection ? (
        <Typography
          data-testid={locators.indexDateSelectionTestId}
          fontSize={fontSize[14]}
          color={theme.palette.text.primary}
          sx={{ marginTop: theme.spacing(2) }}
        >
          <Box component="span" fontWeight={600}>
            {intl.formatMessage({ id: 'projects.cohortBuilder.indexDateSelection.label' })}
          </Box>{' '}
          {props.indexDateSelection}
        </Typography>
      ) : null}

      {/* Card title */}
      <StyledTitleSection
        sx={props.indexDateSelection ? { marginTop: theme.spacing(2) } : undefined}
      >
        <TruncatedTooltip id={`criteriaViewCard-title-tooltip-${id}`} title={title}>
          <Typography
            className="criteria-view-card-title"
            data-testid={locators.titleTestId}
            variant="h6"
            color={theme.palette.text.primary}
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              wordBreak: 'break-word',
              ...(props.onClick && {
                transition: 'color 0.15s ease',
              }),
            }}
          >
            {title}
          </Typography>
        </TruncatedTooltip>
      </StyledTitleSection>

      {/* Details - key-value pairs */}
      {details?.length ? (
        <StyledDetailsSection data-testid={locators.detailsSectionTestId}>
          {details.map((detail, index) => {
            const { key, value, onClick } = detail;

            // Determine the color of the value based on whether it's clickable
            const valueColor = onClick ? theme.palette.primary.main : theme.palette.text.primary;

            return (
              <StyledDetailRow key={`${key}-${index}`}>
                <Box
                  data-testid={locators.detailValueTestId(index)}
                  flex={2}
                  onClick={onClick}
                  sx={{
                    cursor: onClick ? 'pointer' : 'auto',
                    minWidth: 0,
                    paddingRight: theme.spacing(2),
                  }}
                >
                  <Typography
                    variant="body1"
                    color={valueColor}
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 5,
                      WebkitBoxOrient: 'vertical',
                      wordBreak: 'break-word',
                    }}
                  >
                    {value}
                  </Typography>
                </Box>
              </StyledDetailRow>
            );
          })}
        </StyledDetailsSection>
      ) : null}

      {/* Footer actions */}
      {areFooterActionsVisible ? (
        <StyledFooterActions>
          <Stack direction="row" gap={2}>
            {showEditButton ? (
              <Tooltip
                id={`edit-criteria-tooltip-${id}`}
                title={props.actionsDisabled ? props.actionTooltip ?? '' : ''}
              >
                <span>
                  <IconButton
                    data-testid={locators.editButtonTestId}
                    onClick={onEdit}
                    size="small"
                    disabled={props.actionsDisabled}
                    sx={{ '&.Mui-disabled': { opacity: opacity[38] } }}
                  >
                    {props.editIcon ?? (
                      <Icon id={`edit-criteria-${id}`} icon="edit" height={24} width={24} />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            ) : null}

            {showDeleteButton ? (
              <Tooltip
                id={`delete-criteria-tooltip-${id}`}
                title={props.actionsDisabled ? props.actionTooltip ?? '' : ''}
              >
                <span>
                  <IconButton
                    data-testid={locators.deleteButtonTestId}
                    onClick={onDelete}
                    size="small"
                    color="error"
                    disabled={props.actionsDisabled}
                    sx={{ '&.Mui-disabled': { opacity: opacity[38] } }}
                  >
                    <Icon id={`delete-criteria-${id}`} icon="delete" height={24} width={24} />
                  </IconButton>
                </span>
              </Tooltip>
            ) : null}
          </Stack>

          {/* Caller-owned footer extension — used by the AV-criterion
              container to drop the project→org publish (AvScopeControl)
              control into this row once the criterion is library-backed. */}
          {props.additionalFooterActions ? <Box>{props.additionalFooterActions}</Box> : null}
        </StyledFooterActions>
      ) : null}
    </StyledCardContainer>
  );
};
